<template>
  <div class="al">
    <div v-if="!nodeId" class="al__empty">Set OBS device in settings</div>
    <div v-else-if="!channels.length" class="al__empty">
      {{ pending ? 'Listening…' : 'No active audio meters' }}
    </div>
    <div v-else class="al__meters">
      <div v-for="ch in channels" :key="ch.name" class="al__ch">
        <div class="al__bars">
          <div
            v-for="(lvl, li) in ch.levels"
            :key="li"
            class="al__bar-wrap"
          >
            <div class="al__bar-track">
              <div
                class="al__bar-fill"
                :style="{ height: pct(lvl) + '%', background: barGradient(lvl) }"
              />
              <div class="al__peak" :style="{ bottom: pct(ch.peaks[li] || 0) + '%' }" />
            </div>
          </div>
        </div>
        <div class="al__name" :title="ch.name">{{ shortName(ch.name) }}</div>
        <div class="al__db">{{ dbLabel(ch.levels[0]) }}</div>
      </div>
    </div>
    <div v-if="error" class="al__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

const POLL_MS = 1000
const PEAK_DECAY = 0.02
const NOISE_FLOOR = 0.004 // ≈ -48 dB

function mulToDb(mul) {
  const n = Number(mul)
  if (!Number.isFinite(n) || n < NOISE_FLOOR) return -60
  return Math.max(-60, 20 * Math.log10(n))
}

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
    tabActive: { type: Boolean, default: true },
  },
  data() {
    return {
      channels: [],
      pending: '',
      error: '',
      pollTimer: null,
      peaks: {},
      refreshBusy: false,
    }
  },
  computed: {
    nodeId() {
      return this.config.nodeId || ''
    },
    maxChannels() {
      const n = Number(this.config.maxChannels)
      return Number.isFinite(n) && n > 0 ? Math.min(16, Math.round(n)) : 8
    },
    sourceWhitelist() {
      const raw = this.config.sources
      if (!Array.isArray(raw)) return []
      return raw.map((s) => String(s || '').trim()).filter(Boolean)
    },
  },
  watch: {
    tabActive(active) {
      if (!this.nodeId) return
      if (active) this.startPoll()
      else this.stopPoll()
    },
    nodeId: {
      immediate: true,
      handler(id) {
        this.stopPoll()
        this.channels = []
        this.peaks = {}
        this.error = ''
        if (id) {
          this.refresh()
          if (this.tabActive) this.startPoll()
        }
      },
    },
    sourceWhitelist: {
      handler() {
        if (this.nodeId) this.refresh(true)
      },
    },
    maxChannels() {
      if (this.nodeId) this.refresh(true)
    },
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    pct(mul) {
      // map -60dB..0dB to 0..100
      const db = mulToDb(mul)
      return Math.max(0, Math.min(100, ((db + 60) / 60) * 100))
    },
    dbLabel(mul) {
      const db = mulToDb(mul)
      if (db <= -59.5) return '-∞'
      return `${db.toFixed(0)} dB`
    },
    shortName(name) {
      const s = String(name || '')
      return s.length > 6 ? `${s.slice(0, 5)}…` : s
    },
    barGradient(mul) {
      const p = this.pct(mul)
      if (p > 90) return 'linear-gradient(180deg, #ef6e6e 0%, #ffbc34 40%, #22c6ab 100%)'
      if (p > 70) return 'linear-gradient(180deg, #ffbc34 0%, #22c6ab 100%)'
      return 'linear-gradient(180deg, #26c6da 0%, #22c6ab 100%)'
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => {
        if (this.nodeId && !document.hidden && !this.refreshBusy) this.refresh(true)
      }, POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) throw new Error(msg.error || 'Command failed')
      let payload = msg.payload
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload) } catch (e) { return null }
      }
      return payload
    },
    async refresh(soft = false) {
      if (!this.nodeId || this.refreshBusy) return
      this.refreshBusy = true
      if (!soft) this.pending = 'load'
      try {
        const res = await this.sendCommand({
          node_id: this.nodeId,
          adapter: 'obs',
          action: 'GetAudioLevels',
          payload: {},
        })
        const data = this.parsePayload(res) || {}
        const inputs = Array.isArray(data.inputs) ? data.inputs : []
        const allow = this.sourceWhitelist
        let picked = inputs
        if (allow.length) {
          const byName = new Map(inputs.map((inp) => [inp.name || inp.Name || '', inp]))
          picked = allow.map((name) => byName.get(name) || { name, levels: [0] })
        } else {
          picked = inputs.slice(0, this.maxChannels)
        }
        if (picked.length > this.maxChannels) {
          picked = picked.slice(0, this.maxChannels)
        }
        const seen = {}
        const next = picked.map((inp) => {
          const name = inp.name || inp.Name || 'input'
          seen[name] = true
          const levels = (Array.isArray(inp.levels || inp.Levels) ? (inp.levels || inp.Levels) : [0])
            .map((v) => {
              const n = Number(v) || 0
              return n < NOISE_FLOOR ? 0 : Math.min(1, n)
            })
          const prevPeaks = this.peaks[name] || levels.map(() => 0)
          const peaks = levels.map((lvl, i) => {
            const cur = Number(lvl) || 0
            if (cur <= 0) return 0 // silent channel — clear ghost peak
            const peak = prevPeaks[i] || 0
            return cur >= peak ? cur : Math.max(0, peak - PEAK_DECAY)
          })
          this.$set(this.peaks, name, peaks)
          return { name, levels, peaks }
        })
        Object.keys(this.peaks).forEach((name) => {
          if (!seen[name]) this.$delete(this.peaks, name)
        })
        this.channels = next
        if (!soft) this.error = ''
      } catch (e) {
        if (!soft) this.error = e.response?.data?.message || e.message || 'Error'
      } finally {
        this.refreshBusy = false
        if (!soft) this.pending = ''
      }
    },
  },
}
</script>

<style scoped>
.al {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  color: var(--ws-text);
  min-height: 0;
}
.al__empty, .al__error {
  font-size: 0.85em;
  color: var(--ws-text-muted, #6c757d);
}
.al__error { color: var(--ws-error, #ef6e6e); }
.al__meters {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 0.25em;
  overflow-x: auto;
  align-items: stretch;
  padding-bottom: 0.15em;
}
.al__ch {
  flex: 1 1 0;
  min-width: 1.75em;
  max-width: 3.2em;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2em;
}
.al__bars {
  flex: 1;
  min-height: 4em;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 0.15em;
}
.al__bar-wrap {
  width: 0.5em;
  height: 100%;
}
.al__bar-track {
  position: relative;
  height: 100%;
  border-radius: 4px;
  background: var(--ws-ctrl-bg, rgba(128, 128, 128, 0.18));
  overflow: hidden;
}
.al__bar-fill {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 4px;
  transition: height 0.08s linear;
}
.al__peak {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--ws-text, #fff);
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.35);
  transition: bottom 0.08s linear;
}
.al__name {
  font-size: 0.65em;
  font-weight: 700;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ws-text);
}
.al__db {
  font-size: 0.65em;
  font-variant-numeric: tabular-nums;
  color: var(--ws-text-muted);
}
</style>
